import { useCallback, useEffect, useMemo } from 'react';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { useUsersFiltersSlice } from '@restore/usersFilters/usersFilters.slice';
import { PossibleFilters, StateFilterItem } from '@restore/usersFilters/usersFilters.types';

export const useUsersFilters = () => {
  const [
    state,
    toggleActiveFilter,
    allFilters,
    toggleSelectedFilter,
    selectedFilters,
    search,
    setSearch,
    reset,
  ] = useUsersFiltersSlice((state, actions) => [
    state.state,
    actions.toggleActiveFilters,
    state.allFilters,
    actions.toggleSelectedFilters,
    state.selectedFilters,
    state.search,
    actions.setSearch,
    actions.reset,
  ]);

  const subFilters = useMemo(() => {
    return Object.entries(state) as [PossibleFilters, StateFilterItem][];
  }, [state]);

  const filters = useMemo(() => {
    return [
      {
        key: '2',
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: Object.entries(state).map(([key, filter]) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={!!state?.[key as PossibleFilters]?.active} />
            </div>
          ),
        })),
      },
    ];
  }, [state]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const filterOnClick = useCallback(
    (item: MenuInfo) => {
      toggleActiveFilter(item?.key as PossibleFilters);
    },
    [toggleActiveFilter]
  );

  const selectedRole = selectedFilters?.role?.[0];
  const selectedStatus = selectedFilters?.status?.[0];

  const args = useMemo(() => {
    return {
      role: selectedRole,
      status: selectedStatus,
    };
  }, [selectedRole, selectedStatus]);

  return {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    args,
    search,
    setSearch,
  };
};
